import { useEffect, useMemo, useState } from 'react'
import { ErrorConstant, MetaDataService } from 'utility'

const normalizeTimezoneList = result => {
    if (Array.isArray(result)) return result
    if (Array.isArray(result?.Items)) return result.Items
    if (Array.isArray(result?.Timezones)) return result.Timezones
    return []
}

/** Legacy AllTimeZones shape: TimeZoneId + DisplayName */
const toLegacyTimezoneOptions = data =>
    data?.map((ele, i) => ({
        value: ele.TimeZoneId,
        text: ele.DisplayName || ele.TimeZoneId,
        key: `${ele.TimeZoneId}-${i}`,
    })) || []

/**
 * GET /Timezones curated catalog:
 * { IanaName, WindowsId, DisplayName, UtcOffsetLabel, UtcOffsetMinutes }
 */
const toReportTimezoneOptions = data =>
    data?.map((ele, i) => ({
        value: ele.IanaName,
        windowsId: ele.WindowsId,
        text: `${ele.DisplayName} (${ele.UtcOffsetLabel})`,
        key: ele.IanaName || `${ele.WindowsId}-${i}`,
        iana: ele.IanaName,
        utcOffsetLabel: ele.UtcOffsetLabel,
        utcOffsetMinutes: ele.UtcOffsetMinutes,
    })) || []

export const GetTimeZoneList = () => {
    const [data, setData] = useState([])
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(false)
    const options = useMemo(() => toLegacyTimezoneOptions(data), [data])
    const fetchData = async () => {
        try {
            setLoading(true)
            const list = await MetaDataService.getTimeZones()
            setData(normalizeTimezoneList(list))
        } catch {
            setError(ErrorConstant.default)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchData()
    }, [])

    const refetch = () => {
        fetchData()
    }

    return { data, options, setData, error, loading, refetch }
}

/** GET /Timezones — curated catalog for consolidated report send time. */
export const GetReportTimeZoneList = () => {
    const [data, setData] = useState([])
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(false)
    const options = useMemo(() => toReportTimezoneOptions(data), [data])

    const fetchData = async () => {
        try {
            setLoading(true)
            const list = await MetaDataService.getReportTimeZones()
            setData(normalizeTimezoneList(list))
        } catch {
            setError(ErrorConstant.default)
            setData([])
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchData()
    }, [])

    /** Resolve company WindowsId or IanaName to a catalog entry. */
    const findByCompanyTimezone = companyTz => {
        if (!companyTz || !data.length) return null
        return (
            data.find(t => t.WindowsId === companyTz) ||
            data.find(t => t.IanaName === companyTz) ||
            null
        )
    }

    return {
        data,
        options,
        error,
        loading,
        refetch: fetchData,
        findByCompanyTimezone,
    }
}
